import * as React from "react";
import styled, { css } from "styled-components";
import { $enum } from "ts-enum-util";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import {
  Button,
  ButtonType
} from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Colors } from "src/library/utils/theme";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { IList } from "src/core/lists/api/lists.api";
import {
  ISchedule,
  ScheduleStatus as ScheduleStatusType
} from "src/core/schedules/api/schedules.api";
import ScheduleStatusText from "src/core/lists/components/ScheduleStatusText";

interface ListScheduleWrapperButtonProps {
  list: IList;
  terminology: ITerminology;
  isLoading: boolean;
  isScheduleLoading: boolean;
  schedule: ISchedule | null;
  scheduleErrors?: Array<IError<IListErrorData>>;
  setShowStandingOrderDetails: (showStandingOrderDetails: boolean) => void;
  showStandingOrderDetails: boolean;
}

const ListScheduleWrapperButton: React.ReactFCWithChildren<ListScheduleWrapperButtonProps> = props => {
  const {
    list,
    terminology,
    schedule,
    isScheduleLoading = false,
    scheduleErrors,
    isLoading,
    showStandingOrderDetails,
    setShowStandingOrderDetails
  } = props;

  if (!list) {
    return (
      <Text>
        Loading list details...
      </Text>
    );
  }

  const openCloseButton = (
    <Icon
      className="icon"
      icon={showStandingOrderDetails ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
      $fillColor="lightGrey"
      size={24}
    />
  );

  const appIsLoading = isLoading || isScheduleLoading || (schedule && schedule.validating);
  let openCloseButtonShow = false;
  let scheduleStatusData: {
    className: string;
    icon: Icons;
    $fillColor: keyof Colors;
    text: string | ScheduleStatusType;
  } | null = null;

  let buttonData: {
    $buttonType: ButtonType;
    onClick: () => void;
    buttonText: string;
  } | null = null;

  // If the schedule is being updated or the list details are being loaded
  // prevent users from modify the schedule.
  if (appIsLoading) {
    // List / schedule data is loading.
    if (isLoading) {
      scheduleStatusData = {
        className: "update",
        icon: Icons.HOURGLASSFILLED,
        $fillColor: "primaryDark",
        text: "Loading"
      };
    } else if (isScheduleLoading) {
      // Schedule is being updated.
      scheduleStatusData = {
        className: "update",
        icon: Icons.HOURGLASSEMPTY,
        $fillColor: "primaryDark",
        text: "Updating"
      };
    } else if (schedule && schedule.validating) {
      // Schedule orders are being validated..
      scheduleStatusData = {
        className: "update",
        icon: Icons.SYNC,
        $fillColor: "primaryDark",
        text: `Validating ${terminology.schedule.plural.text}`
      };
    }
  } else {
    // If there is an existing schedule.
    if (schedule) {
      // Active schedule.
      if (schedule.status === "active") {
        if (scheduleErrors && scheduleErrors.length) {
          openCloseButtonShow = true;

          scheduleStatusData = {
            className: "error",
            icon: Icons.ERRORCIRCLE,
            $fillColor: "error",
            text: "Has errors"
          };
        } else {
          openCloseButtonShow = true;

          scheduleStatusData = {
            className: "success",
            icon: Icons.TICK,
            $fillColor: "success",
            text: "Active"
          };
        }
      } else {
        // Suspended schedule.
        openCloseButtonShow = true;

        scheduleStatusData = {
          className: "warning",
          icon: Icons.ERRORCIRCLE,
          $fillColor: "error",
          text: "Cancelled"
        };
      }
    } else {
      // Schedule to be created.
      if (showStandingOrderDetails) {
        openCloseButtonShow = true;
      } else {
        buttonData = {
          $buttonType: "secondary",
          onClick: () => setShowStandingOrderDetails(true),
          buttonText: `Create ${terminology.schedule.singular.text}`
        };
      }
    }
  }

  return (
    <>
      {openCloseButtonShow && openCloseButton}

      {scheduleStatusData !== null && (
        <ScheduleStatus className={scheduleStatusData.className}>
          {($enum(ScheduleStatusType).getValues()).includes((scheduleStatusData.text).toLowerCase() as ScheduleStatusType) ?
            (
              <ScheduleStatusText status={(scheduleStatusData.text.toLowerCase()) as ScheduleStatusType} />
            ) : scheduleStatusData.text}

          {" "}

          <Icon
            cursor="default"
            size={16}
            icon={scheduleStatusData.icon}
            $fillColor={scheduleStatusData.$fillColor}
          />
        </ScheduleStatus>
      )}

      {buttonData !== null && (
        <ButtonWrapper>
          <Button
            $buttonType={buttonData.$buttonType}
            onClick={() => (buttonData ? buttonData.onClick() : null)}
          >
            {buttonData.buttonText}
          </Button>
        </ButtonWrapper>
      )}
    </>
  );
};

const ButtonWrapper = styled.div`
  float: right;
  justify-content: flex-end;
  }
`;

const ScheduleStatus = styled(Text)`
  ${({ theme }) => css`
    font-weight: bold;
    font-size: ${theme.fonts.sizes.h4};
    float: right;
    top: 4px;
    position: relative;
    margin-right: ${theme.spacing.padding.sm};
    display: flex;

    > svg {
      margin-left: ${theme.spacing.padding.xs};
    }

    &.success {
      color: ${theme.colors.success};
    }

    &.warning {
      color: ${theme.colors.warn};
    }

    &.update {
      color: ${theme.colors.primaryDark};
    }

    &.error {
      color: ${theme.colors.error};
    }
  `}
`;

export default ListScheduleWrapperButton;
